"use client"

import React, { useState, useEffect } from "react"
import Link from "next/link"
import {
  ArrowRight, Search, ShieldAlert, ShieldCheck, Zap, Mail,
  Globe, Lock, AlertTriangle, Loader2, QrCode, Upload
} from "lucide-react"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"

const PS_BADGES = [
  { label: "PS-01 · Phishing Detection", color: "border-blue-500/40 text-blue-400 bg-blue-500/10" },
  { label: "PS-02 · Bot Detection", color: "border-purple-500/40 text-purple-400 bg-purple-500/10" },
  { label: "PS-03 · Fraud Intelligence", color: "border-red-500/40 text-red-400 bg-red-500/10" },
  { label: "PS-04 · Security Chatbot", color: "border-amber-500/40 text-amber-400 bg-amber-500/10" },
  { label: "PS-05 · Dark Web Monitor", color: "border-emerald-500/40 text-emerald-400 bg-emerald-500/10" },
]

const QUICK_EXAMPLES = [
  "https://paypal-secure-verify.xyz/confirm",
  "https://microsoft-helpdesk.ru/login",
  "Your PayPal account has been limited. Click here to verify...",
]

export function LandingHero() {
  const [input, setInput] = useState("")
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState<{ verdict: string; score: number; event_id: string } | null>(null)
  const [counter, setCounter] = useState<number | null>(null)

  // Fetch real total analyses count from history stats
  useEffect(() => {
    fetch("/api/v1/history/stats")
      .then(r => r.json())
      .then(d => setCounter(d.total_analyses ?? 0))
      .catch(() => setCounter(0))
  }, [])

  const handleScan = async () => {
    if (!input.trim()) return
    setLoading(true); setResult(null)
    try {
      const isUrl = input.includes("http") || input.includes("www.")
      const endpoint = isUrl ? "/api/v1/analyze/url" : "/api/v1/analyze/email"
      const body = isUrl ? { url: input } : { content: input }
      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      })
      if (resp.ok) {
        const data = await resp.json()
        setResult({ verdict: data.verdict, score: data.threat_score, event_id: data.event_id })
      }
    } catch { }
    setLoading(false)
  }

  const verdictConfig = result ? {
    "PHISHING":        { label: "Phishing Detected",   color: "text-red-400",    bg: "bg-red-500/10 border-red-500/30",     icon: ShieldAlert },
    "CRITICAL":        { label: "Critical Threat",      color: "text-red-500",    bg: "bg-red-500/10 border-red-500/30",     icon: ShieldAlert },
    "SUSPICIOUS":      { label: "Suspicious",           color: "text-amber-400",  bg: "bg-amber-500/10 border-amber-500/30", icon: AlertTriangle },
    "SAFE":            { label: "Safe",                 color: "text-emerald-400",bg: "bg-emerald-500/10 border-emerald-500/30", icon: ShieldCheck },
    "CONFIRMED_THREAT":{ label: "Confirmed Threat",     color: "text-red-400",    bg: "bg-red-500/10 border-red-500/30",     icon: ShieldAlert },
    "CLEAN":           { label: "Clean",                color: "text-emerald-400",bg: "bg-emerald-500/10 border-emerald-500/30", icon: ShieldCheck },
  }[result.verdict] || { label: result.verdict, color: "text-slate-400", bg: "bg-card border-border", icon: ShieldCheck } : null

  return (
    <section className="relative min-h-[92vh] flex flex-col items-center justify-center py-28 px-6 overflow-hidden bg-background">
      {/* Background glow */}
      <div className="absolute top-[15%] left-1/2 -translate-x-1/2 h-[700px] w-[900px] bg-blue-500/8 rounded-full blur-[200px] -z-10" />
      <div className="absolute bottom-[-5%] right-[-5%] h-[400px] w-[400px] bg-amber-500/5 rounded-full blur-[150px] -z-10" />
      <div className="absolute inset-0 bg-grid opacity-20 -z-10" />

      <div className="max-w-5xl mx-auto w-full space-y-10 relative z-10">

        {/* PS Badges Row */}
        <div className="flex flex-wrap items-center justify-center gap-2">
          {PS_BADGES.map((b, i) => (
            <Badge key={i} variant="outline" className={cn("px-3 py-1 rounded-full text-[9px] font-black tracking-[0.25em] uppercase", b.color)}>
              {b.label}
            </Badge>
          ))}
        </div>

        {/* Hero Headline */}
        <div className="text-center space-y-5">
          <h1 className="text-5xl md:text-7xl font-black tracking-tighter text-slate-100 leading-[0.9] uppercase">
            Stop Phishing.<br />
            <span className="text-blue-400">In Real Time.</span>
          </h1>
          <p className="text-base md:text-lg text-slate-400 max-w-2xl mx-auto leading-relaxed font-medium">
            5-layer AI fusion: NLP + URL forensics + visual brand matching + email headers + threat intel.
            Paste any URL or email text below — no signup required.
          </p>
        </div>

        {/* Live Counter */}
        <div className="flex items-center justify-center gap-3">
          <div className="h-1.5 w-1.5 rounded-full bg-red-400 animate-ping" />
          <span className="text-[11px] font-black uppercase tracking-widest text-slate-500">
            {counter === null
              ? <span className="text-slate-600 font-mono text-sm">—</span>
              : <span className="text-red-400 font-mono text-sm">{counter.toLocaleString()}</span>
            }{" "}
            {counter === 0 ? "analyses run — submit one above to start" : "phishing analyses completed on this platform"}
          </span>
        </div>

        {/* Instant Demo Widget */}
        <div className="relative">
          <div className="absolute -inset-px rounded-2xl bg-gradient-to-br from-blue-500/20 via-transparent to-amber-500/10 blur-sm" />
          <div className="relative bg-muted/80 backdrop-blur-sm border border-border rounded-2xl p-5 space-y-4">

            <div className="flex items-center gap-2 mb-1">
              <Zap className="h-3.5 w-3.5 text-blue-400" />
              <span className="text-[10px] font-black uppercase tracking-widest text-slate-400">Instant Analysis — No Login Required</span>
            </div>

            <div className="flex gap-3">
              <input
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={e => e.key === "Enter" && handleScan()}
                placeholder="https://suspicious-site.xyz or paste email text..."
                className="flex-1 bg-muted border border-border focus:border-blue-500/40 rounded-xl px-4 h-12 text-sm font-mono text-slate-300 outline-none transition-colors placeholder:text-slate-700"
              />
              <Button
                onClick={handleScan}
                disabled={loading || !input.trim()}
                className="bg-blue-600 hover:bg-blue-500 text-white h-12 px-6 rounded-xl font-black uppercase tracking-widest text-[10px] disabled:opacity-40 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Search className="h-4 w-4 mr-2" />Scan</>}
              </Button>
            </div>

            {/* Quick example chips */}
            <div className="flex flex-wrap gap-2">
              <span className="text-[9px] text-slate-700 uppercase font-bold tracking-widest self-center">Try:</span>
              {QUICK_EXAMPLES.map((ex, i) => (
                <button
                  key={i}
                  onClick={() => setInput(ex)}
                  className="text-[9px] font-mono text-slate-600 hover:text-blue-400 border border-border/50 hover:border-blue-500/20 rounded-lg px-2.5 py-1 transition-all truncate max-w-[220px]"
                >
                  {ex.length > 40 ? ex.slice(0, 40) + "…" : ex}
                </button>
              ))}
            </div>

            {/* Result */}
            {result && verdictConfig && (
              <div className={cn("flex items-center gap-3 px-4 py-3 rounded-xl border animate-in fade-in slide-in-from-bottom-2 duration-300", verdictConfig.bg)}>
                <verdictConfig.icon className={cn("h-5 w-5 shrink-0", verdictConfig.color)} />
                <div className="flex-1 min-w-0">
                  <p className={cn("text-sm font-black uppercase tracking-tight", verdictConfig.color)}>{verdictConfig.label}</p>
                  <p className="text-[10px] text-slate-500 font-mono mt-0.5">Threat score: {Math.round(result.score * 100)}% confidence</p>
                </div>
                <Link href={`/dashboard/analyze?event=${result.event_id}`}>
                  <Button size="sm" className="text-[9px] font-black uppercase tracking-widest rounded-lg h-8 px-3 bg-blue-600 hover:bg-blue-500 text-white shrink-0">
                    Full Report <ArrowRight className="h-3 w-3 ml-1" />
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>

        {/* Feature Chips */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {[
            { icon: Mail, label: "Gmail Integration", desc: "OAuth2 inbox scanning", color: "blue", href: "/dashboard/inbox" },
            { icon: Globe, label: "URL Sandbox", desc: "Deep isolation analysis", color: "purple", href: "/dashboard/sandbox" },
            { icon: QrCode, label: "QR Quishing", desc: "QR code threat detection", color: "amber", href: "/dashboard/analyze" },
            { icon: Upload, label: "Bulk CSV Scan", desc: "100 URLs in parallel", color: "emerald", href: "/dashboard/bulk" },
          ].map((f, i) => (
            <Link key={i} href={f.href}>
              <div className={cn(
                "group border border-border/50 rounded-xl p-4 cursor-pointer transition-all hover:scale-[1.03] bg-white/2 space-y-2",
                f.color === "blue" ? "hover:border-blue-500/30 hover:bg-blue-500/5"
                : f.color === "purple" ? "hover:border-purple-500/30 hover:bg-purple-500/5"
                : f.color === "amber" ? "hover:border-amber-500/30 hover:bg-amber-500/5"
                : "hover:border-emerald-500/30 hover:bg-emerald-500/5"
              )}>
                <div className={cn("h-8 w-8 rounded-lg flex items-center justify-center border",
                  f.color === "blue" ? "bg-blue-500/10 border-blue-500/20 text-blue-400"
                  : f.color === "purple" ? "bg-purple-500/10 border-purple-500/20 text-purple-400"
                  : f.color === "amber" ? "bg-amber-500/10 border-amber-500/20 text-amber-400"
                  : "bg-emerald-500/10 border-emerald-500/20 text-emerald-400"
                )}>
                  <f.icon className="h-4 w-4" />
                </div>
                <p className="text-[11px] font-black uppercase tracking-tight text-slate-300 group-hover:text-foreground transition-colors">{f.label}</p>
                <p className="text-[9px] text-slate-600 uppercase font-bold tracking-widest">{f.desc}</p>
              </div>
            </Link>
          ))}
        </div>

        {/* Bottom CTA */}
        <div className="flex items-center justify-center gap-4 pt-2">
          <Link href="/dashboard">
            <Button className="bg-blue-600 hover:bg-blue-500 text-white rounded-xl px-8 h-11 font-black uppercase tracking-widest text-[10px] shadow-[0_0_20px_rgba(59,130,246,0.3)] hover:scale-105 transition-all">
              Open Dashboard <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
          <Link href="/dashboard/analyze">
            <Button variant="outline" className="border-border hover:border-blue-500/30 text-slate-400 hover:text-blue-400 rounded-xl px-8 h-11 font-black uppercase tracking-widest text-[10px] transition-all">
              Full Analyzer
            </Button>
          </Link>
        </div>

        {/* Trust stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8 pt-6 border-t border-border/50">
          {[
            { value: ">97%", label: "Detection Accuracy" },
            { value: "<2%", label: "False Positive Rate" },
            { value: "<1.5s", label: "Avg. Inference Time" },
            { value: "5-Layer", label: "AI Fusion Pipeline" },
          ].map((s, i) => (
            <div key={i} className="text-center space-y-1">
              <p className="text-2xl font-black tracking-tighter text-blue-400 font-mono">{s.value}</p>
              <p className="text-[9px] font-black uppercase tracking-widest text-slate-600">{s.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
